import React, { useEffect } from 'react';
import '@/App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import DashboardLayout from '@/components/DashboardLayout';
import CookieBanner from '@/components/CookieBanner';
import CommandPalette from '@/components/CommandPalette';
import ShortcutsHUD from '@/components/ShortcutsHUD';
import PwaInstallPrompt from '@/components/PwaInstallPrompt';
import { Toaster } from '@/components/ui/sonner';
import ZertivoLoader from '@/components/ZertivoLoader';
// Lenis (smooth-scroll) is dynamically imported below to keep it out of
// the initial bundle — desktop-only enhancement that adds ~4 KB unused
// for mobile/touch users where it's disabled anyway.

const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const LoginPage = React.lazy(() => import('@/pages/LoginPage'));
const RegisterPage = React.lazy(() => import('@/pages/RegisterPage'));
const MagicLinkCallback = React.lazy(() => import('@/pages/MagicLinkCallback'));
const PublicVerification = React.lazy(() => import('@/pages/PublicVerification'));
const PublicProfile = React.lazy(() => import('@/pages/PublicProfile'));
const PricingPage = React.lazy(() => import('@/pages/PricingPage'));
const ApplicationForm = React.lazy(() => import('@/pages/ApplicationForm'));
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPasswordPage'));
const PublicDirectory = React.lazy(() => import('@/pages/PublicDirectory'));
const SeoScanPage = React.lazy(() => import('@/pages/SeoScanPage'));
const QuickToolsHub = React.lazy(() => import('@/pages/QuickToolsHub'));
const QuickToolsUnlocked = React.lazy(() => import('@/pages/QuickToolsUnlocked'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));
const Datenschutz = React.lazy(() => import('@/pages/Datenschutz'));
const Impressum = React.lazy(() => import('@/pages/Impressum'));
const AdminDashboard = React.lazy(() => import('@/pages/AdminDashboard'));
const CertificateManagement = React.lazy(() => import('@/pages/CertificateManagement'));
const AuditManagement = React.lazy(() => import('@/pages/AuditManagement'));
const CompanyManagement = React.lazy(() => import('@/pages/CompanyManagement'));
const ApplicationManagement = React.lazy(() => import('@/pages/ApplicationManagement'));
const CustomerDashboard = React.lazy(() => import('@/pages/CustomerDashboard'));
const TeamPage = React.lazy(() => import('@/pages/TeamPage'));
const CompanySettingsPage = React.lazy(() => import('@/pages/CompanySettingsPage'));
const AegisPage = React.lazy(() => import('@/pages/AegisPage'));
const AegisConsolePage = React.lazy(() => import('@/pages/AegisConsolePage'));
const MethodologyPage = React.lazy(() => import('@/pages/MethodologyPage'));
const DocsPage = React.lazy(() => import('@/pages/DocsPage'));
const ApiDocsPage = React.lazy(() => import('@/pages/ApiDocsPage'));
const SeoSettingsPage = React.lazy(() => import('@/pages/SeoSettingsPage'));
const BillingSuccessPage = React.lazy(() => import('@/pages/BillingSuccessPage'));
const MyPlanPage = React.lazy(() => import('@/pages/MyPlanPage'));
const AdminSubscriptionsPage = React.lazy(() => import('@/pages/AdminSubscriptionsPage'));
const FaqPage = React.lazy(() => import('@/pages/FaqPage'));
const RankingsHubPage = React.lazy(() => import('@/pages/RankingsHubPage'));
const IndustryRankingPage = React.lazy(() => import('@/pages/IndustryRankingPage'));
const ActivityFeedPage = React.lazy(() => import('@/pages/ActivityFeedPage'));
const EnterpriseDashboard = React.lazy(() => import('@/pages/EnterpriseDashboard'));
const FreeAuditPage = React.lazy(() => import('@/pages/FreeAuditPage'));
const DsgvoCheckPage = React.lazy(() => import('@/pages/DsgvoCheckPage'));
const ComparisonPage = React.lazy(() => import('@/pages/ComparisonPage'));
const IntegrationsPage = React.lazy(() => import('@/pages/IntegrationsPage'));
const EmailDnsSetupPage = React.lazy(() => import('@/pages/EmailDnsSetupPage'));
const AbExperimentsPage = React.lazy(() => import('@/pages/AbExperimentsPage'));
const CertIndustryPage = React.lazy(() => import('@/pages/seo/CertIndustryPage'));
const CityCheckPage = React.lazy(() => import('@/pages/seo/CityCheckPage'));
const GlossaryTermPage = React.lazy(() => import('@/pages/seo/GlossaryTermPage'));
const SeoHubPage = React.lazy(() => import('@/pages/seo/SeoHubPage'));
import Footer from '@/components/Footer';
import AppChrome from '@/components/AppChrome';
const AuditorDashboard = React.lazy(() => import('@/pages/AuditorDashboard'));
const ReportingPage = React.lazy(() => import('@/pages/ReportingPage'));
const UserManagement = React.lazy(() => import('@/pages/UserManagement'));
const TaskManagement = React.lazy(() => import('@/pages/TaskManagement'));
const LogsPage = React.lazy(() => import('@/pages/LogsPage'));
const BadgeEmbed = React.lazy(() => import('@/pages/BadgeEmbed'));
const AGB = React.lazy(() => import('@/pages/AGB'));
const DocumentManagement = React.lazy(() => import('@/pages/DocumentManagement'));
const CRMPipeline = React.lazy(() => import('@/pages/CRMPipeline'));
const WhiteLabelSettings = React.lazy(() => import('@/pages/WhiteLabelSettings'));
const OnboardingWizard = React.lazy(() => import('@/pages/OnboardingWizard'));
const SecuritySettings = React.lazy(() => import('@/pages/SecuritySettings'));
const ProfilePage = React.lazy(() => import('@/pages/ProfilePage'));
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const IndustriesPage = React.lazy(() => import('@/pages/IndustriesPage'));
const ContactPage = React.lazy(() => import('@/pages/ContactPage'));
const SuccessStoriesPage = React.lazy(() => import('@/pages/SuccessStoriesPage'));
const SegmentPage = React.lazy(() => import('@/pages/SegmentPage'));
const InvestorsPage = React.lazy(() => import('@/pages/InvestorsPage'));
const BlogPage = React.lazy(() => import('@/pages/BlogPage'));
const BlogManagement = React.lazy(() => import('@/pages/BlogManagement'));   // legacy, kept as fallback
const SoroBlogRedirect = React.lazy(() => import('@/pages/admin/SoroBlogRedirect'));
const CmsManagement = React.lazy(() => import('@/pages/CmsManagement'));
const StatisticsPage = React.lazy(() => import('@/pages/StatisticsPage'));
const EnterprisePage = React.lazy(() => import('@/pages/EnterprisePage'));
const PartnersPage = React.lazy(() => import('@/pages/PartnersPage'));
const CheckoutPage = React.lazy(() => import('@/pages/CheckoutPage'));
const WalletPage = React.lazy(() => import('@/pages/WalletPage'));
const BoostsPage = React.lazy(() => import('@/pages/BoostsPage'));
const LeadsPage = React.lazy(() => import('@/pages/LeadsPage'));
const OrdersPage = React.lazy(() => import('@/pages/OrdersPage'));
const NetworkPage = React.lazy(() => import('@/pages/NetworkPage'));
const AdminBoostsPage = React.lazy(() => import('@/pages/AdminBoostsPage'));
const AdminRolesPage = React.lazy(() => import('@/pages/AdminRolesPage'));
const AdminCareersPage = React.lazy(() => import('@/pages/AdminCareersPage'));
const AdminPressPage = React.lazy(() => import('@/pages/AdminPressPage'));
const CareersPage = React.lazy(() => import('@/pages/CareersPage'));
const JobDetailPage = React.lazy(() => import('@/pages/JobDetailPage'));
const PressPage = React.lazy(() => import('@/pages/PressPage'));
const PressReleaseDetailPage = React.lazy(() => import('@/pages/PressReleaseDetailPage'));
const AccountingPage = React.lazy(() => import('@/pages/AccountingPage'));
const SupportPage = React.lazy(() => import('@/pages/SupportPage'));
const VerifyEmailPage = React.lazy(() => import('@/pages/VerifyEmailPage'));
const CompanyWizard = React.lazy(() => import('@/pages/CompanyWizard'));
const PoliciesPage = React.lazy(() => import('@/pages/PoliciesPage'));
const AdminSupportPage = React.lazy(() => import('@/pages/AdminSupportPage'));
const StatusPage = React.lazy(() => import('@/pages/StatusPage'));
const StatusManagement = React.lazy(() => import('@/pages/StatusManagement'));
const MyCompanyPage = React.lazy(() => import('@/pages/MyCompanyPage'));
const CompanyAnalytics = React.lazy(() => import('@/pages/CompanyAnalytics'));
const TeamManagement = React.lazy(() => import('@/pages/TeamManagement'));
const InviteManagement = React.lazy(() => import('@/pages/InviteManagement'));
const AdminPocPage = React.lazy(() => import('@/pages/admin/AdminPocPage'));
const AdminPlansEditor = React.lazy(() => import('@/pages/admin/AdminPlansEditor'));
const AdminBillingMetrics = React.lazy(() => import('@/pages/admin/AdminBillingMetrics'));
const AdminReferrals = React.lazy(() => import('@/pages/admin/AdminReferrals'));
const TrustReportLanding = React.lazy(() => import('@/pages/TrustReportLanding'));
const ReferralsDashboard = React.lazy(() => import('@/pages/ReferralsDashboard'));
const AdminCouponsPage = React.lazy(() => import('@/pages/admin/AdminCouponsPage'));
const PocIndexPage = React.lazy(() => import('@/pages/PocIndexPage'));
const PocDocumentPage = React.lazy(() => import('@/pages/PocDocumentPage'));
const LegalDsgvoPage = React.lazy(() => import('@/pages/legal/LegalDsgvoPage'));
const PortalDataPrivacyPage = React.lazy(() => import('@/pages/portal/PortalDataPrivacyPage'));
const InviteAcceptPage = React.lazy(() => import('@/pages/InviteAcceptPage'));

function Loading() {
  // Cinematic Zertivo-branded loader — replaces the generic spinner.
  // Shows the trust-mark animation, italic-green wordmark and a cycling
  // status line ("Vertrauen wird verifiziert", "Audit-Daten werden
  // geladen", …). Used by Suspense fallback and auth-loading gates.
  return <ZertivoLoader />;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Route user to the home screen that matches their role. Staff go to
    // /admin, auditors to /auditor, and everyone else (including the
    // freshly-registered role='user') to /portal — never to /admin since
    // that would just be redirected again and could loop.
    if (['super_admin', 'admin', 'support'].includes(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'auditor') return <Navigate to="/auditor" replace />;
    return <Navigate to="/portal" replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) {
    if (['super_admin', 'admin', 'support'].includes(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'auditor') return <Navigate to="/auditor" replace />;
    // Default — catches role='user' and role='company'. The portal's
    // own CompanyRouterRedirect distinguishes between wizard + dashboard.
    return <Navigate to="/portal" replace />;
  }
  return children;
}

// `user`-role accounts get the company-creation wizard; `company`-role sees
// the full customer dashboard.
function CompanyRouterRedirect() {
  const { user } = useAuth();
  if (user?.role === 'user') return <CompanyWizard />;
  return <CustomerDashboard />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function LenisProvider({ children }) {
  const { pathname } = useLocation();
  // Disable Lenis smooth-scroll inside dashboards (admin/portal/auditor):
  // these have their own internal overflow-y containers which Lenis would
  // otherwise hijack, making the mouse wheel useless on tables and lists.
  const isDashboard = pathname.startsWith('/admin') ||
                      pathname.startsWith('/portal') ||
                      pathname.startsWith('/auditor');

  useEffect(() => {
    if (isDashboard) return undefined;
    // Skip Lenis on touch devices / reduced-motion users.
    // iOS native rubber-band + momentum scroll is better than JS smoothing,
    // and Lenis fights iOS touch gestures (bottom-bar, input blur, etc.).
    const isTouch = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(hover: none), (pointer: coarse), (prefers-reduced-motion: reduce)').matches;
    if (isTouch) return undefined;

    let cleanup;
    let cancelled = false;
    // Defer Lenis until after the page is interactive so it never
    // competes with the LCP / first input.
    const start = () => {
      import('lenis').then(({ default: Lenis }) => {
        if (cancelled) return;
        const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
        function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
        const id = requestAnimationFrame(raf);
        cleanup = () => { cancelAnimationFrame(id); lenis.destroy(); };
      }).catch(() => {/* graceful */});
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 2500 });
    } else {
      setTimeout(start, 1500);
    }
    return () => { cancelled = true; if (cleanup) cleanup(); };
  }, [isDashboard]);

  return children;
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <React.Suspense fallback={<Loading />}>
              <ScrollToTop />
              <LenisProvider>
                <AppChrome>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/methodology" element={<MethodologyPage />} />
                  <Route path="/methodik" element={<Navigate to="/methodology" replace />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/developer-docs" element={<ApiDocsPage />} />
                  <Route path="/api-docs" element={<Navigate to="/developer-docs" replace />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/system-status" element={<StatusPage />} />
                  <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
                  <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />
                  <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
                  <Route path="/verify" element={<PublicVerification />} />
                  <Route path="/verify/:certNumber" element={<PublicVerification />} />
                  <Route path="/profile/:slug" element={<PublicProfile />} />
                  <Route path="/seo-scan" element={<SeoScanPage />} />
                  <Route path="/tools" element={<QuickToolsHub />} />
                  <Route path="/tools/unlocked" element={<QuickToolsUnlocked />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/for/:slug" element={<SegmentPage />} />
                  <Route path="/investors" element={<InvestorsPage />} />
                  <Route path="/aegis" element={<AegisPage />} />
                  <Route path="/apply" element={<ApplicationForm />} />
                  <Route path="/directory" element={<PublicDirectory />} />
                  <Route path="/network" element={<NetworkPage />} />
                  <Route path="/rankings" element={<RankingsHubPage />} />
                  <Route path="/rankings/:industrySlug" element={<IndustryRankingPage />} />
                  <Route path="/activity" element={<ActivityFeedPage />} />
                  <Route path="/audit-check" element={<FreeAuditPage />} />
                  <Route path="/audit-check/dsgvo" element={<DsgvoCheckPage />} />
                  <Route path="/dsgvo-check" element={<Navigate to="/audit-check/dsgvo" replace />} />
                  <Route path="/free-audit" element={<Navigate to="/audit-check" replace />} />
                  <Route path="/trust-report" element={<TrustReportLanding />} />
                  <Route path="/vergleich/:competitor" element={<ComparisonPage />} />
                  <Route path="/cert/:slug" element={<CertIndustryPage />} />
                  <Route path="/check/:slug" element={<CityCheckPage />} />
                  <Route path="/lexikon" element={<SeoHubPage />} />
                  <Route path="/lexikon/:slug" element={<GlossaryTermPage />} />
                  <Route path="/integrations" element={<IntegrationsPage />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/agb" element={<AGB />} />
                  <Route path="/policies" element={<PoliciesPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/industries" element={<IndustriesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/success-stories" element={<SuccessStoriesPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/*" element={<BlogPage />} />
                  <Route path="/statistics" element={<StatisticsPage />} />
                  <Route path="/enterprise" element={<EnterprisePage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/checkout/:planId" element={<CheckoutPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/onboarding" element={<ProtectedRoute allowedRoles={['user', 'company']}><CompanyWizard /></ProtectedRoute>} />
                  <Route path="/forgot-password" element={<AuthRedirect><ResetPasswordPage /></AuthRedirect>} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/admin" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'support']}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/certificates" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><CertificateManagement /></ProtectedRoute>} />
                  <Route path="/admin/audits" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AuditManagement /></ProtectedRoute>} />
                  <Route path="/admin/aegis" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AegisConsolePage /></ProtectedRoute>} />
                  <Route path="/admin/companies" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><CompanyManagement /></ProtectedRoute>} />
                  <Route path="/admin/applications" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'support']}><ApplicationManagement /></ProtectedRoute>} />
                  <Route path="/admin/tasks" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'support']}><TaskManagement /></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['super_admin']}><UserManagement /></ProtectedRoute>} />
                  <Route path="/admin/subscriptions" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminSubscriptionsPage /></ProtectedRoute>} />
                  <Route path="/admin/billing-metrics" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminBillingMetrics /></ProtectedRoute>} />
                  <Route path="/admin/referrals" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminReferrals /></ProtectedRoute>} />
                  <Route path="/admin/plans" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPlansEditor /></ProtectedRoute>} />
                  <Route path="/admin/coupons" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminCouponsPage /></ProtectedRoute>} />
                  <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ReportingPage /></ProtectedRoute>} />
                  <Route path="/admin/logs" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><LogsPage /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['super_admin']}><WhiteLabelSettings /></ProtectedRoute>} />
                  <Route path="/admin/security" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'auditor', 'company']}><SecuritySettings /></ProtectedRoute>} />
                  <Route path="/admin/profile" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'auditor', 'company']}><ProfilePage /></ProtectedRoute>} />
                  <Route path="/admin/pipeline" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'support']}><CRMPipeline /></ProtectedRoute>} />
                  <Route path="/admin/blog" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><SoroBlogRedirect /></ProtectedRoute>} />
                  <Route path="/admin/blog/legacy" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><BlogManagement /></ProtectedRoute>} />
                  <Route path="/admin/content" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><CmsManagement /></ProtectedRoute>} />
                  <Route path="/admin/support" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminSupportPage /></ProtectedRoute>} />
                  <Route path="/admin/status" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><StatusManagement /></ProtectedRoute>} />
                  <Route path="/admin/invites" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><InviteManagement /></ProtectedRoute>} />
                  <Route path="/admin/poc" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminPocPage /></ProtectedRoute>} />
                  <Route path="/poc" element={<PocIndexPage />} />
                  <Route path="/poc/:slug" element={<PocDocumentPage />} />
                  <Route path="/legal/dsgvo" element={<LegalDsgvoPage />} />
                  <Route path="/portal/datenschutz" element={<ProtectedRoute><PortalDataPrivacyPage /></ProtectedRoute>} />
                  <Route path="/invite/:token" element={<InviteAcceptPage />} />
                  <Route path="/auditor" element={<ProtectedRoute allowedRoles={['auditor', 'super_admin', 'admin']}><AuditorDashboard /></ProtectedRoute>} />
                  <Route path="/auditor/audits" element={<ProtectedRoute allowedRoles={['auditor', 'super_admin', 'admin']}><AuditManagement /></ProtectedRoute>} />
                  <Route path="/auditor/tasks" element={<ProtectedRoute allowedRoles={['auditor', 'super_admin', 'admin']}><TaskManagement /></ProtectedRoute>} />
                  <Route path="/portal" element={<ProtectedRoute allowedRoles={['user', 'company']}>{/* user role sees wizard until they've created a company */}<CompanyRouterRedirect /></ProtectedRoute>} />
                  <Route path="/portal/dashboard" element={<ProtectedRoute allowedRoles={['company']}><CustomerDashboard /></ProtectedRoute>} />
                  <Route path="/portal/company" element={<ProtectedRoute allowedRoles={['company']}><MyCompanyPage /></ProtectedRoute>} />
                  <Route path="/portal/analytics" element={<ProtectedRoute allowedRoles={['company']}><CompanyAnalytics /></ProtectedRoute>} />
                  <Route path="/portal/team" element={<ProtectedRoute allowedRoles={['company']}><TeamManagement /></ProtectedRoute>} />
                  <Route path="/portal/enterprise" element={<ProtectedRoute allowedRoles={['company', 'admin', 'super_admin']}><EnterpriseDashboard /></ProtectedRoute>} />
                  <Route path="/portal/certificates" element={<ProtectedRoute allowedRoles={['company']}><CertificateManagement /></ProtectedRoute>} />
                  <Route path="/portal/audits" element={<ProtectedRoute allowedRoles={['company']}><AuditManagement /></ProtectedRoute>} />
                  <Route path="/portal/documents" element={<ProtectedRoute allowedRoles={['company']}><DocumentManagement /></ProtectedRoute>} />
                  <Route path="/portal/billing" element={<ProtectedRoute allowedRoles={['company']}><MyPlanPage /></ProtectedRoute>} />
                  <Route path="/portal/wallet" element={<ProtectedRoute allowedRoles={['company']}><WalletPage /></ProtectedRoute>} />
                  <Route path="/portal/referrals" element={<ProtectedRoute allowedRoles={['company', 'user']}><ReferralsDashboard /></ProtectedRoute>} />
                  <Route path="/portal/boosts" element={<ProtectedRoute allowedRoles={['company']}><BoostsPage /></ProtectedRoute>} />
                  <Route path="/portal/leads" element={<ProtectedRoute allowedRoles={['company']}><LeadsPage /></ProtectedRoute>} />
                  <Route path="/portal/orders" element={<ProtectedRoute allowedRoles={['company']}><OrdersPage /></ProtectedRoute>} />
                  <Route path="/admin/boosts" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminBoostsPage /></ProtectedRoute>} />
                  <Route path="/admin/roles" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminRolesPage /></ProtectedRoute>} />
                  <Route path="/admin/careers" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminCareersPage /></ProtectedRoute>} />
                  <Route path="/admin/press" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AdminPressPage /></ProtectedRoute>} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/careers/:slug" element={<JobDetailPage />} />
                  <Route path="/press" element={<PressPage />} />
                  <Route path="/press/:slug" element={<PressReleaseDetailPage />} />
                  <Route path="/admin/accounting" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AccountingPage /></ProtectedRoute>} />
                  <Route path="/portal/support" element={<ProtectedRoute allowedRoles={['company', 'user']}><SupportPage /></ProtectedRoute>} />
                  <Route path="/support" element={<ProtectedRoute allowedRoles={['company', 'user', 'auditor']}><SupportPage /></ProtectedRoute>} />
                  <Route path="/portal/badges" element={<ProtectedRoute allowedRoles={['company']}><BadgeEmbed /></ProtectedRoute>} />
                  <Route path="/portal/team" element={<ProtectedRoute allowedRoles={['company']}><TeamPage /></ProtectedRoute>} />
                  <Route path="/portal/integrations" element={<ProtectedRoute allowedRoles={['company']}><CompanySettingsPage /></ProtectedRoute>} />
                  <Route path="/admin/team" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TeamPage /></ProtectedRoute>} />
                  <Route path="/admin/seo" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><SeoSettingsPage /></ProtectedRoute>} />
                  <Route path="/admin/email-dns" element={<ProtectedRoute allowedRoles={['super_admin']}><EmailDnsSetupPage /></ProtectedRoute>} />
                  <Route path="/admin/ab-tests" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><AbExperimentsPage /></ProtectedRoute>} />
                  <Route path="/portal/settings" element={<ProtectedRoute allowedRoles={['company']}><CustomerDashboard /></ProtectedRoute>} />
                  <Route path="/billing/success" element={<BillingSuccessPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </AppChrome>
              </LenisProvider>
              <CookieBanner />
              <CommandPalette />
              <ShortcutsHUD />
              <PwaInstallPrompt />
              <Toaster />
            </React.Suspense>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
